import PropTypes from 'prop-types';
import React from 'react';
import { Image } from 'react-bootstrap';
import Carousel from 'nuka-carousel';
import Arrow from './Arrow';
import imageResizer from '../helpers/imageResizer';
import './Slider.scss';

const THREE_UP_RATIO = 1.2;

/*
	SERVER SIDE RENDERING SIZES
	===============================
	#1 Desktop Screen Size: 1920x1080
	#1 Mobile Screen Size: 414x896
*/

// default heights
const DEFAULT_IMAGE_HEIGHT_DESKTOP = 502; // at 1920x1080, this is 3-up height per image
const DEFAULT_IMAGE_HEIGHT_MOBILE = 250;

// default widths
const DEFAULT_IMAGE_WIDTH_DESKTOP = 767; // at 1920x1080, this is 3-up width per image
const DEFAULT_IMAGE_WIDTH_MOBILE = 414;

class Slider extends React.Component {
	constructor(props) {
		super(props);
		const {
			innerWidth,
			currentIndex,
			widgetInfo: {
				vehicle: {
					make,
					model,
					year,
					type,
					bodyStyle,
					trim,
					certified,
					imgAttributes
				}
			},
			labels,
			idcImgAttrFlag
		} = props;
		const typeTerm = type ? labels.get(certified ? 'CERTIFIED' : type) : '';
		const typeTermCapitalCase = typeTerm
			? typeTerm[0].toUpperCase() + typeTerm.substr(1).toLowerCase()
			: '';
		this.imgAlt =
			`${typeTermCapitalCase} ${year} ${make} ${model} ${trim} ${bodyStyle}`.trim();
		this.imgTitle =
			`${typeTermCapitalCase} ${year} ${make} ${model} ${trim} ${bodyStyle}`.trim();
		if (idcImgAttrFlag) {
			this.imgAlt = imgAttributes?.imageAlt;
			this.imgTitle = imgAttributes?.imageTitle;
		}

		this.state = {
			loadedIndex: 3,
			viewportWidth: innerWidth,
			currentIndex: currentIndex || 0
		};

		// TESTING SERVER SIDE RENDERING
		// determine whether this is mobile or desktop,
		// choose the correct height/width
		const {
			widgetInfo: { deviceType }
		} = props;
		this.DEFAULT_IMAGE_HEIGHT =
			deviceType === 'DESKTOP'
				? DEFAULT_IMAGE_HEIGHT_DESKTOP
				: DEFAULT_IMAGE_HEIGHT_MOBILE;
		this.DEFAULT_IMAGE_WIDTH =
			deviceType === 'DESKTOP'
				? DEFAULT_IMAGE_WIDTH_DESKTOP
				: DEFAULT_IMAGE_WIDTH_MOBILE;
	}

	// eslint-disable-next-line
	UNSAFE_componentWillReceiveProps(nextProps) {
		// Set a unique prop for image width so that images don't
		// repaint when the state updates the innerWidth property.
		const newState = { ...this.state };

		const { innerWidth } = nextProps;

		if (!newState.viewportWidth) {
			newState.viewportWidth = innerWidth;
		}

		if (newState.currentIndex !== nextProps.currentIndex) {
			newState.currentIndex = nextProps.currentIndex;

			// if index changes, assume gallery was interacted with, load all images
			newState.loadedIndex = nextProps.images.length;
		}

		this.setState(newState);
	}

	shouldComponentUpdate(nextProps, nextState) {
		// don't care about the method changes (these should maybe be updated to dispatched actions)
		const ignoreProps = ['onCurrentIndexChange', 'onPhotoClick', 'trackGa'];

		const nextPropsKeys = Object.keys(nextProps);
		let hasNewProps = false;
		nextPropsKeys.forEach((propKey) => {
			if (
				ignoreProps.indexOf(propKey) === -1 &&
				nextProps[propKey] !== this.props[propKey]
			) {
				hasNewProps = true;
			}
		});

		const nextStateKeys = Object.keys(nextState);
		let hasNewState = false;
		nextStateKeys.forEach((stateKey) => {
			if (
				stateKey !== 'currentIndex' &&
				nextState[stateKey] !== this.state[stateKey]
			) {
				hasNewState = true;
			}
		});

		return hasNewProps || hasNewState;
	}

	render() {
		const { currentIndex, viewportWidth } = this.state;
		const {
			innerWidth,
			threeUpDisplay,
			imageMode,
			responsiveImage,
			onPhotoClick,
			trackGa,
			images,
			widgetInfo: { deviceType },
			onCurrentIndexChange,
			updateIsBeingDragged,
			isBeingDragged,
			disableAnimation,
			modalSettings: { modalIsOpen },
			onImageError
		} = this.props;

		const numSlides = images.length;
		const slidesToShow = threeUpDisplay ? 3 : 1;
		// Only show arrows if needed
		const showArrows =
			(numSlides > 2 || (!threeUpDisplay && numSlides > 1)) &&
			deviceType === 'DESKTOP';
		const nextArrow = (arrowProps) => (
			<Arrow
				direction="right"
				type="next"
				onClickEvent={() => {
					arrowProps.nextSlide();
					updateIsBeingDragged(false);
					trackGa({
						element: 'Photo Carousel Next button',
						result: 'Move to next photo',
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						}
					});
				}}
			/>
		);
		const prevArrow = (arrowProps) => (
			<Arrow
				direction="left"
				type="prev"
				onClickEvent={() => {
					arrowProps.previousSlide();
					updateIsBeingDragged(false);
					trackGa({
						element: 'Photo Carousel Previous button',
						result: 'Move to previous photo',
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						}
					});
				}}
			/>
		);
		const sliderProps = {
			enableKeyboardControls: false,
			afterSlide: (index) => {
				if (!modalIsOpen && isBeingDragged) {
					trackGa({
						dataToExcludeFromWidgetUserEvent: {
							currentItem: currentIndex + 1,
							totalItems: numSlides
						},
						element: 'Photo Carousel',
						action: 'swiped',
						result: `Moved to photo ${index + 1}`
					});
					updateIsBeingDragged(false);
				}
			},
			onDragStart: () => {
				updateIsBeingDragged(true);
			},
			wrapAround: true,
			disableAnimation,
			speed: 500,
			slideIndex: currentIndex,
			slidesToShow,
			slidesToScroll: 1,
			slideWidth: threeUpDisplay ? THREE_UP_RATIO : 1,
			ref: 'slider',
			// I think the 'current' heightMode makes the most sense for both
			// 3-up and 1-up displays. However, there is currently an issue
			// (https://github.com/FormidableLabs/nuka-carousel/issues/709) where
			// the carousel will disappear when you get to a wrap around image in 3-up.
			// Until that's fixed, use 'first' for 3-up. In 'first', the height is set
			// by the first image. This is the mode already used by the carousel, so the
			// behavior should be fine for non-WIAPI cases. It can cause problems if an
			// image with an odd aspect ratio is added as the first image by the WIAPI.
			// WIAPI usage is currently minimal though, so we're more worried about non-WIAPI
			// cases until the above issue is resolved and we can switch to 'current'.
			// I tried the 'max' heightMode, but got an escalation. Dealers have mixed
			// portrait and landscape images and they didn't like the dead space below the
			// landscape images. It was unclear why it was there. Additionally, since
			// non-visible images are lazy loaded, it would often pop to the weird height
			// after a user interaction. This felt like a bug to people since there was no
			// visible reason why the height suddenly changed.
			heightMode: threeUpDisplay ? 'first' : 'current',
			beforeSlide: (current, newIndex) => {
				if (newIndex !== this.props.currentIndex) {
					onCurrentIndexChange(newIndex);
				}
			},
			renderBottomCenterControls: null,
			renderCenterLeftControls: showArrows ? prevArrow : null,
			renderCenterRightControls: showArrows ? nextArrow : null,
			renderAnnounceSlideMessage: ({ currentSlide, slideCount }) =>
				`${this.imgAlt} Photo ${currentSlide + 1} of ${slideCount}`,
			// For server side rendering
			initialSlideHeight: this.DEFAULT_IMAGE_HEIGHT,
			initialSlideWidth: this.DEFAULT_IMAGE_WIDTH
		};

		const photoClickHandler = (index) => {
			onPhotoClick(index);
		};

		const classes = [];
		if (numSlides === 1 && !imageMode) {
			classes.push('single-slide');
		}

		const blankImage = '/static/images/blank.gif';
		let firstImageSrc = blankImage;

		const slides = images.map((image, index) => {
			const { src } = image;
			const loaded = index <= this.state.loadedIndex;

			let imageSrc = blankImage;
			if (loaded) {
				imageSrc = imageResizer(
					threeUpDisplay,
					viewportWidth,
					src,
					THREE_UP_RATIO
				);
			} else {
				classes.push('d-none');
			}

			// Grab the first image for the background
			if (firstImageSrc === blankImage) {
				firstImageSrc = imageSrc;
			}

			const style = {
				background: `url(${imageSrc}) no-repeat center/cover`,
				// TESTING SERVER SIDE RENDERING
				height: `${this.DEFAULT_IMAGE_HEIGHT}px`
			};

			const onImageErrorCallback = () => {
				onImageError(src, imageSrc);
			};

			let imageComponent;
			if (imageMode) {
				imageComponent = (
					<Image
						key={imageSrc}
						src={imageSrc}
						className={classes.join(' ')}
						onLoad={() => {
							// Fire a resize event to force a redraw of the carousel so it can recalculate the height
							if (index === 0) {
								window.dispatchEvent(new Event('resize'));
							}
						}}
						onError={onImageErrorCallback}
						onClick={() => {
							photoClickHandler(index);
						}}
						role="presentation"
						alt={this.imgAlt}
						title={this.imgTitle}
					/>
				);
				if (!responsiveImage && !threeUpDisplay) {
					// Wrap component to use the image as its own background
					imageComponent = (
						<div
							className="blurred-container"
							key={`container-${imageSrc}`}
						>
							<div
								style={style}
								className="blurred-background"
								aria-label={this.imgAlt}
							/>
							{imageComponent}
						</div>
					);
				}
			} else {
				imageComponent = (
					<div
						key={imageSrc}
						style={style}
						className={classes.join(' ')}
						onClick={() => {
							photoClickHandler(index);
						}}
						role="presentation"
						aria-label={this.imgAlt}
					>
						<Image
							src={imageSrc}
							className="d-none"
							onError={onImageErrorCallback}
						/>
					</div>
				);
			}
			return imageComponent;
		});

		const carouselClasses = [
			'photo-carousel',
			!imageMode || !innerWidth ? 'background-mode' : 'image-mode',
			threeUpDisplay ? 'add-border' : ''
		];

		let carouselComponent;
		if (!responsiveImage && threeUpDisplay) {
			// Use first image for background
			const carouselStyle = {
				background: `url(${firstImageSrc}) no-repeat right/contain`,
				// TESTING SERVER SIDE RENDERING
				height: `${this.DEFAULT_IMAGE_HEIGHT}px`
			};
			carouselComponent = (
				// Add blurred background
				<div className="blurred-container">
					<div style={carouselStyle} className="blurred-background" />
					<div className={carouselClasses.join(' ')}>
						<Carousel {...sliderProps}>{slides}</Carousel>
					</div>
				</div>
			);
		} else {
			carouselComponent = (
				<div className={carouselClasses.join(' ')}>
					<Carousel {...sliderProps}>{slides}</Carousel>
				</div>
			);
		}
		return carouselComponent;
	}
}

Slider.defaultProps = {
	currentIndex: 0,
	nextSlide: () => {},
	previousSlide: () => {},
	onPhotoClick: () => {},
	updateIsBeingDragged: () => {},
	isBeingDragged: false,
	modalSettings: {
		modalIsOpen: false
	},
	onImageError: () => {}
};

Slider.propTypes = {
	labels: PropTypes.shape({
		has: PropTypes.func,
		get: PropTypes.func,
		getKeys: PropTypes.func,
		getAll: PropTypes.func,
		getHTML: PropTypes.func
	}).isRequired,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			thumbnail: PropTypes.string
		})
	).isRequired,
	modalSettings: PropTypes.shape({
		modalIsOpen: PropTypes.bool
	}),

	// renderSettings
	innerHeight: PropTypes.number.isRequired,
	innerWidth: PropTypes.number.isRequired,
	imageMode: PropTypes.bool.isRequired,
	responsiveImage: PropTypes.bool.isRequired,
	threeUpDisplay: PropTypes.bool.isRequired,
	currentIndex: PropTypes.number,
	onCurrentIndexChange: PropTypes.func.isRequired,

	nextSlide: PropTypes.func,
	previousSlide: PropTypes.func,
	trackGa: PropTypes.func.isRequired,
	onPhotoClick: PropTypes.func,
	widgetInfo: PropTypes.shape({
		deviceType: PropTypes.string,
		vehicle: PropTypes.shape({
			make: PropTypes.string,
			model: PropTypes.string,
			year: PropTypes.string,
			type: PropTypes.string,
			bodyStyle: PropTypes.string,
			trim: PropTypes.string,
			certified: PropTypes.bool,
			imgAttributes: PropTypes.shape({
				imageAlt: PropTypes.string,
				imageTitle: PropTypes.string
			})
		}).isRequired
	}).isRequired,
	updateIsBeingDragged: PropTypes.func,
	isBeingDragged: PropTypes.bool,
	disableAnimation: PropTypes.bool,
	onImageError: PropTypes.func,
	idcImgAttrFlag: PropTypes.bool.isRequired
};

export default Slider;
